import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Loader2, Plus, Crown, History, MessageSquare } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Help me build my first monthly budget",
  "Practice a job interview with me",
  "Explain how taxes work on my paycheck",
  "How do I negotiate a higher salary?",
  "Give me advice on setting boundaries",
];

const newSessionId = () => `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function Coach() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(newSessionId());
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState("gemini");
  const [streaming, setStreaming] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const scrollRef = useRef(null);
  const starterFired = useRef(false);

  const loadSessions = useCallback(() => {
    api.get("/coach/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const openSession = async (sid) => {
    setSheetOpen(false);
    if (sid === sessionId && messages.length) return;
    try {
      const { data } = await api.get(`/coach/history/${sid}`);
      setSessionId(sid);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
    } catch { toast.error("Could not load chat"); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    const isNew = messages.length === 0;
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ session_id: sessionId, message: msg, model }),
      });
      if (res.status === 402) {
        const err = await res.json();
        setMessages((m) => m.slice(0, -1));
        toast.error(err.detail || "Free limit reached", { action: { label: "Go Premium", onClick: () => navigate("/app/premium") } });
        setStreaming(false);
        return;
      }
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
      if (isNew) loadSessions();
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }; return c; });
    } finally { setStreaming(false); }
  };

  const reset = () => { setSessionId(newSessionId()); setMessages([]); setSheetOpen(false); };

  useEffect(() => {
    const starter = location.state?.starter;
    if (starter && !starterFired.current) {
      starterFired.current = true;
      navigate(".", { replace: true, state: {} });
      send(starter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const SessionList = () => (
    <div className="space-y-1.5">
      <button data-testid="new-chat-side-btn" onClick={reset} className="w-full flex items-center gap-2 rounded-2xl px-3 py-2.5 bg-violet-600/15 border border-violet-500/25 text-violet-300 font-semibold text-sm hover:bg-violet-600/25 transition-colors">
        <Plus size={16} /> New chat
      </button>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground px-2 pt-3 pb-1">Past chats</div>
      {sessions.length === 0 && <p className="text-xs text-muted-foreground px-2 py-2">No saved chats yet.</p>}
      {sessions.map((s) => (
        <button key={s.session_id} data-testid={`session-${s.session_id}`} onClick={() => openSession(s.session_id)}
          className={`w-full flex items-center gap-2 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors ${s.session_id === sessionId ? "bg-white/10 text-foreground" : "hover:bg-white/5 text-muted-foreground"}`}>
          <MessageSquare size={15} className="shrink-0" />
          <span className="truncate">{s.title || "New chat"}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex gap-5 h-[calc(100vh-8.5rem)]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 glass rounded-3xl p-3 overflow-y-auto no-scrollbar">
        <SessionList />
      </aside>

      {/* Main chat */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 grid place-items-center shadow-lg shadow-violet-500/30"><Sparkles size={22} className="text-white" /></div>
            <div>
              <h1 className="font-display font-extrabold text-xl tracking-tight leading-none">Pilot · AI Coach</h1>
              <span className="text-xs text-muted-foreground">Your 24/7 life mentor</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile history */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button data-testid="history-btn" className="lg:hidden h-9 w-9 grid place-items-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10"><History size={18} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="glass border-white/10 w-72">
                <SheetHeader><SheetTitle className="font-display">Chat History</SheetTitle></SheetHeader>
                <div className="mt-4"><SessionList /></div>
              </SheetContent>
            </Sheet>
            <div className="flex p-0.5 rounded-full bg-white/5 border border-white/10">
              {[{ k: "gemini", l: "Gemini" }, { k: "claude", l: "Claude" }].map((m) => (
                <button key={m.k} data-testid={`model-${m.k}`} onClick={() => setModel(m.k)} className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${model === m.k ? "bg-violet-600 text-white" : "text-muted-foreground"}`}>{m.l}</button>
              ))}
            </div>
            <button data-testid="new-chat-btn" onClick={reset} className="h-9 w-9 grid place-items-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10"><Plus size={18} /></button>
          </div>
        </div>

        {!user?.is_premium && (
          <button onClick={() => navigate("/app/premium")} data-testid="coach-premium-banner" className="mb-3 flex items-center gap-2 text-xs glass rounded-full px-4 py-2 border border-yellow-500/20 self-start">
            <Crown size={14} className="text-yellow-400" /> Free plan: 5 messages per chat · <span className="text-yellow-400 font-bold">Upgrade for unlimited</span>
          </button>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar space-y-4 pb-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="h-16 w-16 rounded-3xl bg-gradient-to-br from-violet-500 to-indigo-600 grid place-items-center shadow-xl shadow-violet-500/30 mb-4"><Sparkles size={30} className="text-white" /></div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-1">How can I help, {user?.name?.split(" ")[0]}?</h2>
              <p className="text-muted-foreground text-sm mb-6 max-w-sm">Ask me anything about money, career, relationships or life. I know your goals and progress.</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid={`suggestion-${s.slice(0, 6)}`} onClick={() => send(s)} className="text-xs font-semibold px-3.5 py-2 rounded-full bg-white/5 border border-white/10 hover:border-violet-500/40 transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-3xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-md" : "glass rounded-bl-md"}`}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="animate-spin" size={16} /> : "")}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="glass rounded-3xl p-2 flex items-end gap-2 border border-white/10">
          <Textarea
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask your coach anything…"
            rows={1}
            className="flex-1 resize-none bg-transparent border-0 focus-visible:ring-0 min-h-[44px] max-h-32 py-2.5"
          />
          <Button data-testid="coach-send-btn" onClick={() => send()} disabled={streaming || !input.trim()} className="h-11 w-11 rounded-2xl bg-violet-600 hover:bg-violet-500 shrink-0 p-0">
            {streaming ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          </Button>
        </div>
      </div>
    </div>
  );
}
